import { useEffect, useMemo, useRef, useState } from "react"
import { useSession } from "../auth/session"
import { fetchRestaurants } from "../network/auth"
import useCloseRequest from "../hooks/useCloseRequest"
import Loader from "../components/Loader"
import RemoteImage from "../components/RemoteImage"
import "./RestaurantsPage.css"

export default function RestaurantsPage({ onDone, onCancel }) {
  const { restaurant: currentRestaurant, selectRestaurant, signOut } = useSession()
  const [restaurants, setRestaurants] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isSearching, setIsSearching] = useState(false)
  const [query, setQuery] = useState("")
  const searchRef = useRef(null)

  useEffect(() => {
    let cancelled = false
    fetchRestaurants()
      .then((list) => {
        if (!cancelled) setRestaurants(list)
      })
      .catch((requestError) => {
        if (!cancelled) setError(requestError)
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (isSearching) searchRef.current?.focus()
  }, [isSearching])

  const closeSearch = () => {
    setQuery("")
    setIsSearching(false)
  }
  useCloseRequest(isSearching, closeSearch)

  const visible = useMemo(() => {
    const term = query.trim().toLowerCase()
    if (!term) return restaurants
    return restaurants.filter((entry) => (entry.name ?? "").toLowerCase().includes(term))
  }, [restaurants, query])

  return (
    <div className="restaurants">
      <header className="restaurants__bar">
        {isSearching ? (
          <input
            ref={searchRef}
            className="restaurants__search t-p16"
            type="search"
            value={query}
            placeholder="Search..."
            autoCapitalize="none"
            autoCorrect="off"
            onChange={(event) => setQuery(event.target.value)}
          />
        ) : (
          <button
            type="button"
            className="restaurants__back t-p16"
            onClick={onCancel ?? signOut}
          >
            <img src="/img/back.png" alt="" aria-hidden="true" />
            {onCancel ? "Go Back" : "Log Out"}
          </button>
        )}

        <button
          type="button"
          className="restaurants__search-button"
          aria-label={isSearching ? "Close search" : "Search restaurants"}
          onClick={isSearching ? closeSearch : () => setIsSearching(true)}
        >
          {isSearching ? <CrossIcon /> : <SearchIcon />}
        </button>
      </header>

      <div className="restaurants__rule" />

      <div className="restaurants__body scroll-y">
        <h1 className="restaurants__title t-y24">Select Restaurant</h1>
        <p className="restaurants__hint">
          You can always switch to a different restaurant from the menu
        </p>

        <p className="restaurants__section t-p16">All Restaurants</p>

        {isLoading && <Loader label="Loading restaurants" />}
        {!isLoading && error && <p className="restaurants__empty t-y24">{error.message}</p>}
        {!isLoading && !error && !visible.length && (
          <p className="restaurants__empty t-y24">No Restaurants Available</p>
        )}

        {!isLoading && !error && visible.length > 0 && (
          <ul className="restaurants__grid">
            {visible.map((entry) => {
              const isSelected = entry.id === currentRestaurant?.id
              return (
                <li key={entry.id}>
                  <button
                    type="button"
                    className={`restaurant-card${isSelected ? " restaurant-card--selected" : ""}`}
                    onClick={() => {
                      selectRestaurant(entry)
                      onDone()
                    }}
                  >
                    <RemoteImage className="restaurant-card__icon" src={entry.icon} alt="" />
                    <span className="restaurant-card__name t-s14">{entry.name}</span>
                    {entry.location && (
                      <span className="restaurant-card__address">{entry.location}</span>
                    )}
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}

function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.8" />
      <path d="m16.5 16.5 4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  )
}

function CrossIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="m6 6 12 12M18 6 6 18"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  )
}
